import useSessionStorage from "@mirage/hooks/useSessionStorage";
import ApplicationService from "@mirage/services/ApplicationService";
import { useCurrentApplicationStore } from "@mirage/store/store";

export interface HorizonMessage {
    Method: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Argument: any;
}

const VIEWER_WINDOW_NAME = "mirageDocumentViewer";
let viewerWindow: Window | null = null;
let retryTimer: ReturnType<typeof setInterval> | null = null;
let readyListener: ((event: MessageEvent) => void) | null = null;

function buildMessage(attachmentIds: number[], editable = false): HorizonMessage {
    const applicationService = new ApplicationService();
    const message: HorizonMessage = {
        Method: "OpenAttachment",
        Argument: {
            AttachmentIds: attachmentIds,
            UserId: 0,
            HideDetach: true,
            UserName: "",
        },
    };

    if (editable) {
        const user = applicationService.getLoggedInUser();
        if (user) {
            message.Argument.UserId = user.id;
            message.Argument.UserName = `${user.first_name} ${user.last_name}`;
        }
    }

    return message;
}

function cleanup() {
    if (retryTimer) {
        clearInterval(retryTimer);
        retryTimer = null;
    }
    if (readyListener) {
        window.removeEventListener("message", readyListener);
        readyListener = null;
    }
}

function openOrMessageViewer(viewerUrl: string, message: HorizonMessage) {
    cleanup();

    const isOpen = viewerWindow != null && !viewerWindow.closed;

    if (isOpen && viewerWindow) {
        viewerWindow.postMessage(message, "*");
        viewerWindow.focus();
    } else {
        viewerWindow = window.open(viewerUrl, VIEWER_WINDOW_NAME);
        if (!viewerWindow) {
            return;
        }

        // Listen for the "ready" callback from the viewer API (IsReady response).
        // Only send the actual OpenAttachment message once the viewer confirms it's ready.
        readyListener = (event: MessageEvent) => {
            if (event.data && event.data.callback === "ready") {
                cleanup();
                viewerWindow?.postMessage(message, "*");
            }
        };
        window.addEventListener("message", readyListener);

        // Poll with IsReady until the viewer responds.
        // The viewer app needs time to load and initialize before it can handle messages.
        let attempts = 0;
        retryTimer = setInterval(() => {
            attempts++;
            try {
                viewerWindow?.postMessage({ Method: "IsReady" }, "*");
            } catch {
                // Ignore errors during initial load
            }
            if (attempts >= 50 || !viewerWindow || viewerWindow.closed) {
                cleanup();
            }
        }, 200);
    }
}

export const useDocumentViewer = (editable = false) => {
    const currentAppName = useCurrentApplicationStore((s) => s.currentApplication);
    const [sessionValue] = useSessionStorage(currentAppName);
    const { clientSettings } = sessionValue;
    const showAttachments = (attachmentIds: number[]) => {
        const viewerUrl = clientSettings?.DOCUMENT_VIEWER_URL;
        if (!viewerUrl || attachmentIds.length === 0) {
            return;
        }

        const message = buildMessage(attachmentIds, editable);
        openOrMessageViewer(viewerUrl, message);
    };

    return { showAttachments };
};
